'use client';

import { ReferenceItem } from '@/app/services/llm';
import { Skeleton } from '@/components/ui/skeleton';
import { getOpenAlexId } from '@/lib/reference-identifiers';
import {
  getZhReferenceTranslation,
  shouldShowZhReferenceTranslation,
} from '@/lib/reference-translation-display';
import { cn } from '@/lib/utils';
import copy from 'copy-to-clipboard';
import Link from 'next/link';
import { ReactNode } from 'react';
import { toast } from 'sonner';

function formatAuthors(authors: ReferenceItem['authors'] | undefined): string {
  if (!authors || authors.length === 0) return '';
  const names = authors.slice(0, 3).map((author) => {
    const directName = author.name?.trim();
    if (directName) return directName;
    const foreName = author.fore_name?.trim();
    const lastName = author.last_name?.trim();
    return [foreName, lastName].filter(Boolean).join(' ');
  });
  if (authors.length > 3) {
    names.push('et al.');
  }
  return names.filter(Boolean).join(', ');
}

function buildCitationLine(item: ReferenceItem): string {
  const source = item.pub_source_str?.trim() || '';
  return source;
}

function buildAbstractPreview(abstract: string | undefined): string {
  if (!abstract) return '';
  return abstract.replace(/\s+/g, ' ').trim();
}

function formatSearchGateway(item: ReferenceItem): string {
  const source = getReferenceSource(item);
  if (source === 'pubmed') return 'PubMed';
  if (source === 'openalex') return 'OpenAlex';
  return item.search_gateway?.trim() || 'PubMed';
}

function getReferenceSource(item: ReferenceItem): 'pubmed' | 'openalex' {
  const normalized = item.search_gateway?.trim().toLowerCase();
  if (normalized === 'pubmed') return 'pubmed';
  if (normalized === 'openalex' || getOpenAlexId(item)) return 'openalex';
  return 'pubmed';
}

function SourceBadge({ value }: { value: string }) {
  return (
    <span className="ml-2 inline-flex translate-y-[-1px] items-center rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-medium leading-none text-primary">
      {value}
    </span>
  );
}

function IdentifierChip({ label, value }: { label: string; value: string }) {
  return (
    <button
      type="button"
      className="relative z-20 inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-foreground transition-colors hover:bg-muted"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        copy(value);
        toast.success(`${label} 已复制`);
      }}
    >
      {label}: {value}
    </button>
  );
}

export default function PaperLink({
  item,
  index,
  actionCtx,
  abstractCtx,
  className,
  href,
  onClick,
  showDetails = false,
}: {
  item: ReferenceItem;
  index: number;
  actionCtx?: ReactNode;
  abstractCtx?: ReactNode;
  className?: string;
  href?: string;
  onClick?: () => void;
  showDetails?: boolean;
}) {
  const zhTranslation = getZhReferenceTranslation(item);
  const translatedTitle = zhTranslation?.title?.trim();
  const originalTitle = item.title?.trim() || translatedTitle || '';
  const showTranslatedTitle =
    shouldShowZhReferenceTranslation(item) && Boolean(translatedTitle);
  const displayTitle = showTranslatedTitle
    ? `${originalTitle} ${translatedTitle}`
    : originalTitle;
  const displayAbstract = item.abstract;
  const authors = formatAuthors(item.authors);
  const citationLine = buildCitationLine(item);
  const abstractPreview = buildAbstractPreview(displayAbstract);
  const searchGateway = formatSearchGateway(item);
  const source = getReferenceSource(item);
  const openAlexId = getOpenAlexId(item);
  const showPmid = source === 'pubmed' && Boolean(item.pmid);
  const showOpenAlexId = source === 'openalex' && Boolean(openAlexId);
  const hasIdentifiers = Boolean(showPmid || showOpenAlexId || item.doi);
  const footerCtx = actionCtx ?? abstractCtx;
  const isClickable = Boolean(href || onClick);

  return (
    <div
      className={cn(
        'group relative rounded-xl border border-border/50 bg-background px-4 py-3 transition-colors duration-150',
        'hover:border-border hover:bg-muted/30',
        isClickable && 'cursor-pointer',
        className,
      )}
      key={index}
      onClick={!href ? onClick : undefined}
    >
      {href && (
        <Link
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={displayTitle}
          className="absolute inset-0 z-10 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        />
      )}
      <div className="relative gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-col">
            <h4 className="text-[15px] font-semibold leading-6 text-foreground">
              <span className="float-left mr-2 mt-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground text-[10px] font-medium leading-none text-background">
                {index + 1}
              </span>
              {originalTitle}
              {searchGateway && <SourceBadge value={searchGateway} />}
              {showTranslatedTitle && (
                <span className="mt-1 block text-[13px] font-medium leading-5 text-primary">
                  <span className="mr-1.5 inline-flex translate-y-[-1px] items-center rounded-full border border-primary/25 bg-primary/8 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary">
                    译
                  </span>
                  {translatedTitle}
                </span>
              )}
            </h4>
          </div>

          {(authors ||
            citationLine ||
            hasIdentifiers ||
            (showDetails && abstractPreview)) && (
            <div className="mt-2 space-y-1">
              {authors && (
                <div className="line-clamp-2 text-[12px] leading-5 text-foreground/85">
                  {authors}
                </div>
              )}
              {citationLine && (
                <div className="text-[12px] leading-5 text-primary">
                  {citationLine}
                </div>
              )}
              {showDetails && abstractPreview && (
                <div className="line-clamp-3 pt-1 text-xs leading-5 text-foreground/80">
                  {abstractPreview}
                </div>
              )}
              {hasIdentifiers && (
                <div className="flex flex-wrap gap-1.5 pt-1.5">
                  {showPmid && (
                    <IdentifierChip label="PMID" value={item.pmid} />
                  )}
                  {showOpenAlexId && (
                    <IdentifierChip label="OpenAlex ID" value={openAlexId} />
                  )}
                  {item.doi && <IdentifierChip label="DOI" value={item.doi} />}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {footerCtx && (
        <div className="relative z-20 mt-3 border-t border-border/50 pt-2.5">
          {footerCtx}
        </div>
      )}
    </div>
  );
}

export function PaperLinkSkeleton({
  showAbstractCtx = false,
}: {
  showAbstractCtx?: boolean;
}) {
  return (
    <div className="relative flex p-3 rounded-xl bg-card justify-between items-start gap-x-1">
      <Skeleton className="inline-block min-w-5 h-5 shrink-0 rounded-full" />
      <div className="flex-1 -mt-1 space-y-2">
        <Skeleton className="h-4 w-full rounded" />
        <Skeleton className="h-3 w-2/3 rounded" />
      </div>
      {showAbstractCtx && <Skeleton className="h-8 w-8 rounded" />}
    </div>
  );
}
